import React from 'react';
import { ChevronRight } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { PremiumBadge } from '@/components/ui/PremiumBadge';
import type { BrowseInstructor } from '../hooks/useBrowseInstructors';

export type ServiceFilter = 'all' | 'video_review' | 'private_classes';

// Tiny per-mode verb shown next to the chevron so the card itself confirms
// the action that the surrounding tap-hint declared.
const ACTION_KEY: Record<ServiceFilter, string> = {
  all:             'browse_instructors.card_action_view_profile',
  video_review:    'browse_instructors.card_action_request_review',
  private_classes: 'browse_instructors.card_action_view_schedule',
};

interface InstructorBrowseCardProps {
  instructor: BrowseInstructor;
  activeFilter: ServiceFilter;
  onClick: () => void;
}

const CURRENCY_SYMBOL: Record<string, string> = { BRL: 'R$', USD: '$', EUR: '€' };

function parseBeltColor(beltRank?: string | null): string {
  if (!beltRank) return 'white';
  const rank = beltRank.toLowerCase();
  if (rank.includes('black'))  return 'black';
  if (rank.includes('brown'))  return 'brown';
  if (rank.includes('purple')) return 'purple';
  if (rank.includes('blue'))   return 'blue';
  return 'white';
}

function getInitials(fullName?: string | null): string {
  if (!fullName) return '?';
  return fullName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
}

function formatClassPrice(value: number | null, currency: string | null, unit: string): string {
  if (!value || value === 0) return '';
  const symbol = CURRENCY_SYMBOL[currency ?? ''] ?? (currency ?? '');
  return `${symbol} ${value.toFixed(0)} ${currency ?? ''} / ${unit}`;
}

function formatCentsPrice(cents: number | null, currency: string | null, unit: string): string {
  if (!cents || cents === 0) return '';
  const symbol = CURRENCY_SYMBOL[currency ?? ''] ?? (currency ?? '');
  return `${symbol} ${(cents / 100).toFixed(2)} ${currency ?? ''} / ${unit}`;
}

export const InstructorBrowseCard: React.FC<InstructorBrowseCardProps> = ({
  instructor,
  activeFilter,
  onClick,
}) => {
  const { t } = useI18n();

  const showPrice   = activeFilter !== 'all';
  const locationLine = [instructor.jiu_jitsu_school, instructor.city]
    .filter(Boolean)
    .join(' · ');

  let priceText: string | null = null;
  if (showPrice) {
    if (activeFilter === 'video_review') {
      const formatted = formatCentsPrice(
        instructor.roll_review_price_cents,
        instructor.roll_review_currency,
        t('browse_instructors.per_review'),
      );
      priceText = formatted || t('browse_instructors.price_not_set');
    } else if (activeFilter === 'private_classes') {
      const formatted = formatClassPrice(
        instructor.class_value,
        instructor.class_currency,
        t('browse_instructors.per_session'),
      );
      priceText = formatted || t('browse_instructors.price_not_set');
    }
  }

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 py-4 text-left hover:bg-white/5 active:bg-white/10 transition-colors"
    >
      <FramedAvatar
        src={instructor.profile_picture_url ?? undefined}
        alt={instructor.full_name}
        initials={getInitials(instructor.full_name)}
        beltRank={parseBeltColor(instructor.belt_rank)}
        frameSize={{ w: 56, h: 62 }}
        isManual
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="text-base font-bold text-white leading-tight truncate">
            {instructor.full_name}
          </p>
          {instructor.is_premium && (
            <PremiumBadge variant="outlined" size="xs" className="flex-shrink-0" />
          )}
        </div>
        {locationLine && (
          <p className="text-xs text-white/40 mt-0.5 truncate">{locationLine}</p>
        )}
        {priceText && (
          <p className="text-xs font-medium mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {priceText}
          </p>
        )}
      </div>

      {/* Trailing affordance — declares the verb (Request review / View
          schedule / View profile) inline so the card itself confirms what
          tapping it will do. Kept tiny on purpose to avoid clutter. */}
      <div className="flex items-center gap-1 flex-shrink-0 text-white/50">
        <span className="hidden sm:inline text-xs">
          {t(ACTION_KEY[activeFilter])}
        </span>
        <ChevronRight className="w-4 h-4" />
      </div>
    </button>
  );
};
